import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { IoShieldCheckmarkOutline } from "react-icons/io5";
import { useAuth } from "@/context/AuthContext";
import { myCarnetAccessRequests, respondCarnetAccess } from "@/lib/api";

const NERODUS_IMG = "/nerodus.png";

/**
 * Pop-up centrale (Nerodus) qui prévient le membre dès qu'un administrateur
 * demande l'accès complet à son carnet, et permet d'autoriser directement.
 * Montée globalement dans DojoShell pour tous les membres connectés.
 */
export default function AccessRequestPrompt() {
  const { isAuthenticated, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [queue, setQueue] = useState([]);     // demandes en attente non traitées
  const [dismissed, setDismissed] = useState([]); // reportées pour cette session
  const [busy, setBusy] = useState(false);

  const fetchPending = async () => {
    try {
      const reqs = await myCarnetAccessRequests();
      setQueue((reqs || []).filter((r) => r.status === "pending"));
    } catch { /* silencieux */ }
  };

  useEffect(() => {
    if (!isAuthenticated || isAdmin) return undefined;
    fetchPending();
    const id = setInterval(fetchPending, 20000);
    return () => clearInterval(id);
  }, [isAuthenticated, isAdmin]);

  if (!isAuthenticated || isAdmin) return null;
  const current = queue.find((r) => !dismissed.includes(r.id));
  if (!current) return null;

  const answer = async (approve) => {
    setBusy(true);
    try {
      await respondCarnetAccess(current.id, approve);
      setQueue((q) => q.filter((r) => r.id !== current.id));
      toast.success(approve ? "Accès accordé au Dōjō ✓" : "Demande refusée");
    } catch { toast.error("Action impossible, réessaie"); } finally { setBusy(false); }
  };

  return (
    <div style={s.overlay} data-testid="access-prompt-overlay">
      <div style={s.card} data-testid="access-prompt-card" role="dialog" aria-modal="true">
        <div style={s.glow} />
        <img src={NERODUS_IMG} alt="Nerodus" style={s.avatar} onError={(e) => { e.target.style.display = "none"; }} />
        <div style={s.name}>Nerodus</div>
        <div style={s.role}>Gardien du Dōjō</div>
        <h2 style={s.title}>Une demande d'accès à ton carnet</h2>
        <p style={s.body}>
          <strong>{current.admin_name || "L'administrateur"}</strong> aimerait pouvoir ajouter et organiser des éléments
          dans <strong>ton carnet</strong>, comme si c'était le sien. Tu gardes le contrôle : tu peux retirer cet accès à tout moment.
        </p>
        <div style={s.actions}>
          <button style={s.deny} data-testid="access-prompt-deny" disabled={busy} onClick={() => answer(false)}>Refuser</button>
          <button style={s.allow} data-testid="access-prompt-allow" disabled={busy} onClick={() => answer(true)}>
            <IoShieldCheckmarkOutline size={18} /> {busy ? "…" : "Autoriser l'accès"}
          </button>
        </div>
        <button style={s.later} data-testid="access-prompt-later" disabled={busy} onClick={() => setDismissed((d) => [...d, current.id])}>
          Plus tard
        </button>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: "fixed", inset: 0, zIndex: 7000, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18, animation: "accFade .25s ease" },
  card: { position: "relative", overflow: "hidden", width: "100%", maxWidth: 440, textAlign: "center", padding: "34px 28px 24px", borderRadius: 24, border: "1px solid color-mix(in srgb, var(--brand) 40%, var(--border-c))", background: "var(--bg-secondary)", boxShadow: "0 40px 100px rgba(0,0,0,0.5)", animation: "accPop .32s cubic-bezier(.2,.9,.3,1.2) both" },
  glow: { position: "absolute", top: -90, left: "50%", transform: "translateX(-50%)", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 30%, transparent), transparent 70%)" },
  avatar: { position: "relative", zIndex: 1, width: 92, height: 92, borderRadius: "50%", objectFit: "cover", border: "3px solid var(--brand)", boxShadow: "0 10px 30px rgba(0,0,0,0.35)" },
  name: { position: "relative", zIndex: 1, marginTop: 12, fontSize: 18, fontWeight: 900, color: "var(--text-primary)", fontFamily: "var(--font-display)" },
  role: { position: "relative", zIndex: 1, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "var(--brand)" },
  title: { position: "relative", zIndex: 1, margin: "16px 0 8px", fontSize: 20, fontWeight: 900, color: "var(--text-primary)" },
  body: { position: "relative", zIndex: 1, fontSize: 14.5, lineHeight: 1.6, color: "var(--text-secondary)", margin: "0 auto 22px", maxWidth: 360 },
  actions: { position: "relative", zIndex: 1, display: "flex", gap: 10, justifyContent: "center" },
  allow: { display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 22px", borderRadius: 999, border: "none", background: "var(--brand)", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer" },
  deny: { padding: "13px 20px", borderRadius: 999, border: "1.5px solid var(--border-c)", background: "transparent", color: "var(--text-secondary)", fontWeight: 800, fontSize: 14, cursor: "pointer" },
  later: { position: "relative", zIndex: 1, marginTop: 14, background: "none", border: "none", color: "var(--text-secondary)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", textDecoration: "underline" },
};

if (typeof document !== "undefined" && !document.getElementById("acc-prompt-kf")) {
  const el = document.createElement("style");
  el.id = "acc-prompt-kf";
  el.textContent = "@keyframes accFade{from{opacity:0}to{opacity:1}}@keyframes accPop{from{opacity:0;transform:translateY(16px) scale(.96)}to{opacity:1;transform:none}}";
  document.head.appendChild(el);
}
